import { ChevronRight, Package } from "lucide-react";
import React from "react";

import type { CompareRow, ReceiptCard, ReceiptStatus } from "@/api/transfer_receipts";
import type { TransferSessionHeader } from "@/api/transfer_check";
import { Card } from "@/components/ui/card";
import { fmtDateTime } from "@/lib/format";
import { REMAINING_EPS, norm } from "@/lib/order-check";
import { fmtQty } from "@/lib/scan-ui/status";
import { cn } from "@/lib/utils";

/**
 * Общее для «Приёмки перемещений» и «Расхождений перемещений»: карточка
 * списка, шапка перемещения и таблица «Отправлено / Принято / Разница».
 * Сравнение считает БЭК (`comparison` — одна функция для экрана и «Принять»);
 * экран его только рисует, своего расчёта не держит.
 */

type BadgeView = { label: string; cls: string };

/**
 * Номер перемещения в заголовках и карточках: по заказу — номер заказа 1С,
 * без заказа (`free`) — номер «Перемещения» портала (`transfer_number` у него
 * пуст). Одна функция на списки, приёмку и разбор.
 */
export function receiptTitle(
  x: Pick<ReceiptCard, "kind" | "transfer_number" | "display_number">,
): string {
  return x.kind === "free" ? `Перемещение №${x.display_number ?? "—"}` : x.transfer_number;
}

const WARN = "bg-amber-200 text-amber-900";

/**
 * Бейдж карточки — по статусу И доведённости до 1С: `completed` без
 * проведения — не «Принято» (документа в учёте нет), а «Не проведено»;
 * решение «провести по принятому», чьи шаги 1С не доведены, — «Ждёт
 * проведения» (повтор того же действия доделывает).
 */
export function receiptBadge(row: Pick<ReceiptCard, "status" | "resolution" | "transfer_doc_posted">): BadgeView {
  const done = row.status === "completed" && row.transfer_doc_posted;
  if (row.resolution === "by_receipt" && !done) return { label: "Ждёт проведения", cls: WARN };
  if (row.status === "completed" && !row.transfer_doc_posted) return { label: "Не проведено", cls: WARN };
  if (row.status === "sent" && row.resolution === "match") return { label: "Не проведено", cls: WARN };
  const byStatus: Record<ReceiptStatus, BadgeView> = {
    sent: { label: "Едет", cls: "bg-sky-100 text-sky-800" },
    discrepancy: { label: "Расхождение — на разборе", cls: WARN },
    completed: { label: "Принято", cls: "bg-emerald-100 text-emerald-800" },
  };
  return byStatus[row.status];
}

export function ReceiptStatusBadge({ row }: { row: ReceiptCard }): React.ReactElement {
  const v = receiptBadge(row);
  return (
    <span data-testid="receipt-badge" className={cn("rounded px-1.5 py-0.5 text-[10px] font-semibold", v.cls)}>
      {v.label}
    </span>
  );
}

/**
 * Маршрут двумя строками: «откуда» / «→ куда», с ПЕРЕНОСОМ, без многоточия.
 * На 360 px два длинных имени склада в одну строку обрезались до «Склад…», и
 * получатель не мог прочитать, куда едет перемещение. Тёмная верхняя панель
 * сканера держит одну обрезанную строку — там место под чип сканера, а
 * полный маршрут — в инфо-блоке под ней (`TransferFacts`).
 */
export function RouteLines({
  from,
  to,
  className,
}: {
  from: string | null | undefined;
  to: string | null | undefined;
  className?: string;
}): React.ReactElement {
  return (
    <div className={cn("leading-snug [overflow-wrap:anywhere]", className)} data-testid="route">
      <div data-testid="route-from">
        <span>{from || "—"}</span>
      </div>
      <div data-testid="route-to" className="font-semibold">
        <span aria-hidden="true">→ </span>
        <span>{to || "—"}</span>
      </div>
    </div>
  );
}

/** Карточка перемещения в списке: откуда → куда, кто и когда отправил,
 *  заказ, сколько позиций и штук. */
export function ReceiptCardView({
  row,
  onClick,
}: {
  row: ReceiptCard;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button type="button" onClick={onClick} className="w-full text-left" data-testid="receipt-card">
      <Card className="flex items-center gap-3 p-3 transition-colors hover:bg-accent active:bg-accent">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{receiptTitle(row)}</span>
            {row.kind === "free" && <span className="text-xs text-muted-foreground">без заказа</span>}
            {row.kind === "order" && row.display_number != null && (
              <span className="text-xs text-muted-foreground">проверка №{row.display_number}</span>
            )}
            <ReceiptStatusBadge row={row} />
          </div>
          <RouteLines from={row.from_wh_name} to={row.to_wh_name} className="text-sm" />
          <div className="text-xs text-muted-foreground">
            Отправил {row.sent_by_name || "—"} · {fmtDateTime(row.sent_at) || "—"}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Package className="size-3" />
              {row.positions} поз. · {fmtQty(row.qty_total)}
            </span>
            {row.organization_name && <span className="truncate">{row.organization_name}</span>}
            {row.status === "completed" && row.transfer_doc_number && (
              <span className={row.transfer_doc_posted ? "text-emerald-700" : "font-semibold text-amber-800"}>
                Перемещение № {row.transfer_doc_number}
                {row.transfer_doc_posted ? "" : " — не проведено"}
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
      </Card>
    </button>
  );
}

/** Откуда → куда, кто и когда отправил, номер заказа (без заказа — так и
 *  сказано) — шапка карточки. */
export function TransferFacts({
  header,
  className,
}: {
  header: TransferSessionHeader;
  className?: string;
}): React.ReactElement {
  return (
    <div className={cn("space-y-0.5 text-[12px]", className)} data-testid="transfer-facts">
      <RouteLines from={header.from_name} to={header.to_name} />
      <div className="opacity-80">
        Отправил {header.sent_by_name || "—"} · {fmtDateTime(header.sent_at) || "—"}
      </div>
      <div className="opacity-80" data-testid="transfer-basis">
        {header.kind === "free" ? "Без заказа" : `Заказ ${header.transfer_number}`}
        {header.organization_name ? ` · ${header.organization_name}` : ""}
      </div>
    </div>
  );
}

/** Строка — расхождение: разница в количестве ИЛИ не те партии (бэк ставит
 *  `partia_mismatch` и при `diff = 0`). */
export function hasDiff(r: CompareRow): boolean {
  return Math.abs(r.diff) > REMAINING_EPS || r.partia_mismatch;
}

/** Ключ строки сравнения — тот же, что у бэка: (номенклатура, характеристика);
 *  партии сведены внутри строки. */
export function compareKey(nom: string | null, char: string | null): string {
  return `${nom ?? ""}|${norm(char)}`;
}

/** «10 шт»; единицы нет — просто число (слово наугад не подставляем). */
export function withUnit(qty: number, unit: string | null | undefined): string {
  return unit ? `${fmtQty(qty)} ${unit}` : fmtQty(qty);
}

/** Единица ИТОГА — только если у ВСЕХ строк она одна и непустая; иначе "". */
export function commonUnit(units: (string | null | undefined)[]): string {
  const names = new Set(units.map((u) => u ?? ""));
  return names.size === 1 ? ([...names][0] ?? "") : "";
}

function DiffCell({
  diff,
  partiaMismatch = false,
  unit = "",
}: {
  diff: number;
  partiaMismatch?: boolean;
  /** Базовая единица строки — подписывает разницу; пусто — число без слова. */
  unit?: string;
}): React.ReactElement {
  if (Math.abs(diff) <= REMAINING_EPS) {
    return partiaMismatch ? (
      <span className="text-amber-800">парт.</span>
    ) : (
      <span className="text-emerald-700">✓</span>
    );
  }
  return (
    <span className={diff > 0 ? "text-amber-800" : "text-red-700"}>
      {diff > 0 ? "+" : "−"}
      {withUnit(Math.abs(diff), unit)}
    </span>
  );
}

/**
 * Таблица сравнения «Отправлено / Принято / Разница» (базовые единицы).
 * Строки с разницей подсвечены и идут первыми — на них и смотрят. Настоящая
 * таблица с авто-раскладкой: числовые колонки — по ширине самого длинного
 * значения (`whitespace-nowrap`, «125.375», «123456» влезают на 360 px), а
 * колонка позиции забирает остаток и переносится. Фиксированные 3rem
 * обрезали дробные и шестизначные количества.
 */
export function CompareTable({
  rows,
  highlightKey,
}: {
  rows: CompareRow[];
  /** Строка последнего скана — выделяется рамкой. */
  highlightKey?: string;
}): React.ReactElement {
  const sorted = [...rows].sort(
    (a, b) =>
      Number(hasDiff(b)) - Number(hasDiff(a)) ||
      a.nomenclature_name.localeCompare(b.nomenclature_name),
  );
  const sent = rows.reduce((s, r) => s + r.sent_qty, 0);
  const received = rows.reduce((s, r) => s + r.received_qty, 0);
  // Итог складывает разные номенклатуры: подписываем единицей, только когда
  // она у всех строк одна (иначе число без слова — см. `commonUnit`).
  const totalUnit = commonUnit(sorted.map((r) => r.base_unit_name));
  const num = "w-px whitespace-nowrap py-1.5 pl-2 text-right align-middle font-mono tabular-nums";
  return (
    <div className="overflow-hidden rounded-lg border bg-white">
      <table className="w-full border-collapse" data-testid="compare-table">
        <thead>
          <tr className="border-b bg-slate-100 text-[10px] font-bold uppercase text-slate-500">
            <th className="px-2 py-1.5 text-left font-bold">Позиция</th>
            <th className={cn(num, "font-sans font-bold")}>Отпр.</th>
            <th className={cn(num, "font-sans font-bold")}>Прин.</th>
            <th className={cn(num, "pr-2 font-sans font-bold")}>Разн.</th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <td colSpan={4} className="px-2 py-4 text-center text-[12px] text-slate-400">
                Позиций нет
              </td>
            </tr>
          )}
          {sorted.map((r) => {
            const key = compareKey(r.nomenclature_ref, r.characteristic_ref);
            const diff = hasDiff(r);
            return (
              <tr
                key={key}
                data-testid="compare-row"
                data-diff={diff ? "1" : "0"}
                data-highlight={highlightKey === key ? "1" : undefined}
                className={cn(
                  "border-b last:border-b-0",
                  diff ? (r.diff < -REMAINING_EPS ? "bg-red-50" : "bg-amber-50") : "",
                  // Рамка — тенью ячеек: `outline` у строки таблицы рисуют не все движки.
                  highlightKey === key &&
                    "[&>td]:shadow-[inset_0_2px_0_#0ea5e9,inset_0_-2px_0_#0ea5e9] [&>td:first-child]:shadow-[inset_2px_2px_0_#0ea5e9,inset_0_-2px_0_#0ea5e9] [&>td:last-child]:shadow-[inset_-2px_2px_0_#0ea5e9,inset_0_-2px_0_#0ea5e9]",
                )}
              >
                <td className="px-2 py-1.5 align-middle [overflow-wrap:anywhere]">
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="font-mono text-[13px] font-bold">{r.nomenclature_article}</span>
                    {r.characteristic_name && (
                      <span className="rounded border bg-white px-1 text-[10px]">{r.characteristic_name}</span>
                    )}
                    {r.partia_name && (
                      <span className="rounded border bg-white px-1 font-mono text-[10px]">
                        парт. {r.partia_name}
                      </span>
                    )}
                  </div>
                  <div className="line-clamp-2 text-[11px] text-slate-600">{r.nomenclature_name}</div>
                  {r.partia_mismatch && r.partia_note && (
                    <div data-testid="partia-note" className="text-[11px] font-semibold text-amber-800">
                      {r.partia_note}
                    </div>
                  )}
                </td>
                <td className={cn(num, "text-[14px]")}>{withUnit(r.sent_qty, r.base_unit_name)}</td>
                <td className={cn(num, "text-[14px] font-bold")}>{withUnit(r.received_qty, r.base_unit_name)}</td>
                <td className={cn(num, "pr-2 text-[14px] font-bold")}>
                  <DiffCell diff={r.diff} partiaMismatch={r.partia_mismatch} unit={r.base_unit_name} />
                </td>
              </tr>
            );
          })}
        </tbody>
        {sorted.length > 0 && (
          <tfoot>
            <tr className="border-t bg-slate-50 text-[12px] font-bold">
              <td className="px-2 py-1.5">Итого</td>
              <td className={num}>{withUnit(sent, totalUnit)}</td>
              <td className={num}>{withUnit(received, totalUnit)}</td>
              <td className={cn(num, "pr-2")}>
                <DiffCell diff={received - sent} unit={totalUnit} />
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
